<template>
	<AppCardPage class="existing-mortgage-page">
		<ExistingMortgageCard
			v-for="(loan, idx) in dossierDataStore.data.existingMortgages"
			:key="loan?.id"
			v-model="dossierDataStore.data.existingMortgages[idx]"
			:collapsed="
				loan.id !== lastAddedId &&
				dossierDataStore.data.existingMortgages.length > 1
			"
			@delete="dossierDataStore.data.existingMortgages.splice(idx, 1)"
		/>

		<OrphanInformatieAanvraagRow
			v-for="informatieAanvraag in orphanInformatieAanvragen"
			:key="informatieAanvraag.id"
			:informatie-aanvraag="informatieAanvraag"
			@add="addExistingMortgageFromInformatieAanvraag(informatieAanvraag)"
		/>

		<AppButton
			variant="ghost"
			icon="add"
			class="border border-dashed rounded p-4 text-gray-600 enabled:hover:border-gray-400"
			@click="addExistingMortgage"
		>
			Huidige hypotheek toevoegen
		</AppButton>

		<pre v-if="showValues">{{
			JSON.stringify(dossierDataStore.data.existingMortgages, null, 2)
		}}</pre>
	</AppCardPage>
</template>

<script setup lang="ts">
import { mapInformatieBerichtToExistingMortgage } from "@/helpers/hdnInformatieMapper";
import {
	type InformatieAanvraagWithDossier,
	getMortgageOwnerFromInformatieAanvraag,
	informatieAanvraagMatchesMortgage,
} from "@/components/existing-mortgage/informatie-aanvraag-status";
import { useDossierDataStore } from "@/store/useDossierDataStore";
import { useDossierStore } from "@/store/useDossierStore";
import type { ExistingMortgage } from "@loanlink-nl/model-app";
import { mutators, queries } from "@loanlink-nl/zero";
import { inject, computed, ref, watch } from "vue";
import { useQuery, useZero } from "@/composables/useZero";
import { toast } from "vue-sonner";

const showValues = inject("DEBUG_SHOW_MODELVALUES", false);

const dossierDataStore = useDossierDataStore();
const dossierStore = useDossierStore();
const z = useZero();
const lastAddedId = ref<string>();
const pendingLinkedInformatieAanvraagIds = ref(new Set<string>());

const { data: informatieAanvragen } = useQuery(() =>
	queries.informatieAanvragen.forDossier({
		dossierId: dossierStore.selectedDossier?.id ?? "",
	}),
);

const unlinkedInformatieAanvragen = computed(() => {
	if (!informatieAanvragen.value) {
		return [];
	}

	return informatieAanvragen.value.filter(
		(informatieAanvraag) =>
			!dossierDataStore.data.existingMortgages.some((existingMortgage) =>
				informatieAanvraagMatchesMortgage(informatieAanvraag, existingMortgage),
			),
	);
});

const orphanInformatieAanvragen = computed(() =>
	unlinkedInformatieAanvragen.value.filter(
		(informatieAanvraag) =>
			!pendingLinkedInformatieAanvraagIds.value.has(informatieAanvraag.id),
	),
);

const defaultCurrentObjectId = computed(() => {
	const objects = dossierDataStore.data.currentObjects?.objects ?? [];
	return objects.length === 1 ? objects[0]?.id : undefined;
});

watch(unlinkedInformatieAanvragen, (informatieAanvragen) => {
	const linkedIds = new Set(informatieAanvragen.map((item) => item.id));
	pendingLinkedInformatieAanvraagIds.value = new Set(
		[...pendingLinkedInformatieAanvraagIds.value].filter((id) =>
			linkedIds.has(id),
		),
	);
});

function addExistingMortgage() {
	lastAddedId.value = crypto.randomUUID();
	dossierDataStore.data.existingMortgages ??= [];
	dossierDataStore.data.existingMortgages.push({
		id: lastAddedId.value,
		kadasterRanking: 1,
		currentObjectId: defaultCurrentObjectId.value,
		mortgageParts: [
			{
				id: crypto.randomUUID(),
				interestDeductionLaw: "new",
			},
		],
	});
}

async function addExistingMortgageFromInformatieAanvraag(
	informatieAanvraag: InformatieAanvraagWithDossier,
) {
	lastAddedId.value = crypto.randomUUID();
	const newExistingMortgageId = lastAddedId.value;

	const fallbackMortgage: ExistingMortgage = {
		id: newExistingMortgageId,
		kadasterRanking: 1,
		currentObjectId: defaultCurrentObjectId.value,
		lenderCode: informatieAanvraag.content.lenderCode,
		lenderMortgageId: informatieAanvraag.content.mortgageNumber,
		owner: getMortgageOwnerFromInformatieAanvraag(
			informatieAanvraag,
			dossierDataStore.data.customers,
		),
		mortgageParts: [
			{
				id: crypto.randomUUID(),
				interestDeductionLaw: "new",
			},
		],
	};

	const mappedMortgage = informatieAanvraag.response
		? mapInformatieBerichtToExistingMortgage({
				existingMortgage: fallbackMortgage,
				informatieBericht: informatieAanvraag.response,
				mortgageNumber: informatieAanvraag.content.mortgageNumber,
				expectedLenderCode: informatieAanvraag.content.lenderCode,
			})
		: undefined;

	dossierDataStore.data.existingMortgages ??= [];
	dossierDataStore.data.existingMortgages.push(
		mappedMortgage?.status === "mapped"
			? mappedMortgage.mortgage
			: fallbackMortgage,
	);
	pendingLinkedInformatieAanvraagIds.value = new Set([
		...pendingLinkedInformatieAanvraagIds.value,
		informatieAanvraag.id,
	]);

	try {
		z.value.mutate(
			mutators.hdnInformatieAanvraagTable.setExistingMortgage({
				id: informatieAanvraag.id,
				existingMortgageId: newExistingMortgageId,
			}),
		);
	} catch {
		pendingLinkedInformatieAanvraagIds.value = new Set(
			[...pendingLinkedInformatieAanvraagIds.value].filter(
				(id) => id !== informatieAanvraag.id,
			),
		);
		dossierDataStore.data.existingMortgages =
			dossierDataStore.data.existingMortgages.filter(
				(mortgage) => mortgage.id !== newExistingMortgageId,
			);

		toast.error("Hypotheek toevoegen is niet gelukt", {
			description:
				"De informatieaanvraag kon niet gekoppeld worden aan de nieuwe hypotheek.",
		});
	}
}
</script>

<style scoped lang="scss">
.existing-mortgage-page {
	:deep(.readonly-bordered-input input[readonly]),
	:deep(.readonly-bordered-input input:disabled),
	:deep(.readonly-bordered-input select:disabled) {
		border: 1px solid var(--border);
		background-color: var(--hover-muted);
		color: var(--text-muted);
		cursor: not-allowed;
	}

	:deep(.readonly-bordered-input .symbol) {
		color: var(--text-muted);
	}

	:deep(.readonly-bordered-input .radio-option[aria-disabled="true"]) {
		background: var(--hover-muted);
	}

	:deep(
		.readonly-bordered-input
			.radio-option[aria-disabled="true"][aria-checked="true"]
	) {
		background: var(--background-primary);
		color: var(--text-muted);
	}
}
</style>
